import type { FormEvent, ReactNode } from "react";

import { Box, Divider, Group, Text } from "metabase/ui";

import { renderDefaultSubmitButton } from "../../utils";
import { TimeToggle } from "../TimeToggle";
import { clearTimePart } from "../utils";

import { SingleDatePickerBody } from "./SingleDatePickerBody";
import type { SingleDatePickerValue } from "./types";

interface SingleDatePickerProps {
  value: SingleDatePickerValue;
  hasTimeToggle: boolean;
  renderSubmitButton?: () => ReactNode;
  onChange: (value: SingleDatePickerValue) => void;
  onSubmit: () => void;
  timezone?: string;
  timezoneLabel?: string;
}

export function SingleDatePicker({
  value,
  hasTimeToggle,
  renderSubmitButton = renderDefaultSubmitButton,
  onChange,
  onSubmit,
  timezone,
  timezoneLabel,
}: SingleDatePickerProps) {
  const { date, hasTime } = value;
  const displayTimezone = timezoneLabel ?? timezone;
  const hasFooterInfo = hasTimeToggle || displayTimezone;

  const handleDateChange = (newDate: Date) => {
    onChange({ date: newDate, hasTime });
  };

  const handleTimeToggle = () => {
    onChange({ date: clearTimePart(date), hasTime: !hasTime });
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    onSubmit();
  };

  return (
    <form onSubmit={handleSubmit}>
      <Box p="md">
        <SingleDatePickerBody
          value={date}
          hasTime={hasTime}
          onChange={handleDateChange}
        />
      </Box>
      <Divider />
      <Group
        p="sm"
        justify={hasFooterInfo ? "space-between" : "flex-end"}
        wrap="wrap"
      >
        {hasFooterInfo && (
          <Group gap="sm" wrap="wrap">
            {hasTimeToggle && (
              <TimeToggle hasTime={hasTime} onClick={handleTimeToggle} />
            )}
            {displayTimezone && (
              <Text
                c="text-medium"
                size="sm"
                style={{ overflowWrap: "anywhere" }}
              >
                {displayTimezone}
              </Text>
            )}
          </Group>
        )}
        {renderSubmitButton()}
      </Group>
    </form>
  );
}
